import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Edit } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

const ReceiptsEditForm = ({ receipt, onUpdate, onClose, operators, flags, paymentMethods }) => {
    const { toast } = useToast();
    const [formData, setFormData] = useState({});
    const [availableFlags, setAvailableFlags] = useState([]);

    const formatValueForInput = (value) => {
        if (value === null || value === undefined) return '';
        const numberValue = Number(value);
        if (isNaN(numberValue)) return '';
        return numberValue.toFixed(2).replace('.', ',');
    };

    const initializeFormData = (receiptData) => {
        if (!receiptData) return {};
        const operatorId = receiptData.operator_id || (receiptData.operators ? operators.find(o => o.name === receiptData.operators.name)?.id : '');

        return {
            data_pagamento: receiptData.data_pagamento ? receiptData.data_pagamento.split('T')[0] : '',
            operator_id: operatorId,
            bandeira: receiptData.bandeira || '',
            tipo: receiptData.tipo || '',
            parcelas: receiptData.parcelas?.toString() || '1',
            valor: formatValueForInput(receiptData.valor),
            codigo_autorizacao: receiptData.codigo_autorizacao || '',
            codigopedido: receiptData.codigopedido || '',
        };
    };

    useEffect(() => {
        if (receipt) {
            setFormData(initializeFormData(receipt));
        }
    }, [receipt, operators]);

    useEffect(() => {
        if (!formData.operator_id) {
            setAvailableFlags(flags);
            return;
        }

        const selectedOp = operators.find(op => op.id === formData.operator_id);
        const linkedFlagIds = selectedOp?.flagIds || [];

        // Separar bandeiras vinculadas das não vinculadas
        const linkedFlags = flags.filter(flag => linkedFlagIds.includes(flag.id));
        const otherFlags = flags.filter(flag => !linkedFlagIds.includes(flag.id));

        // Unir as duas listas colocando as vinculadas no topo
        setAvailableFlags([...linkedFlags, ...otherFlags]);
    }, [formData.operator_id, operators, flags]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleOperatorChange = (value) => {
        handleInputChange('operator_id', Number(value));
        handleInputChange('bandeira', '');
    };

    const handleUpdate = (e) => {
        e.preventDefault();
        if (!formData.data_pagamento || !formData.bandeira || !formData.valor || !formData.operator_id || !formData.tipo) {
            toast({ title: "Erro", description: "Por favor, preencha todos os campos obrigatórios (*).", variant: "destructive" });
            return;
        }
        onUpdate(formData, receipt.id, {
            onSuccess: onClose
        });
    };

    return (
        <>
            <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-xl"><Edit className="w-5 h-5" /> Editar Recebimento</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="data_pagamento">Data do Pagamento *</Label>
                        <Input id="data_pagamento" type="date" value={formData.data_pagamento || ''} onChange={(e) => handleInputChange('data_pagamento', e.target.value)} required />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="operator_id">Operadora *</Label>
                        <Select value={formData.operator_id?.toString() || ''} onValueChange={handleOperatorChange}>
                            <SelectTrigger><SelectValue placeholder="Selecione a operadora" /></SelectTrigger>
                            <SelectContent>{operators.map((op) => (<SelectItem key={op.id} value={op.id.toString()}>{op.name}</SelectItem>))}</SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="bandeira">Bandeira *</Label>
                        <Select value={formData.bandeira || ''} onValueChange={(value) => handleInputChange('bandeira', value)} disabled={!formData.operator_id}>
                            <SelectTrigger><SelectValue placeholder="Selecione a bandeira" /></SelectTrigger>
                            <SelectContent className="max-h-[300px]">{availableFlags.map((flag) => (<SelectItem key={flag.id} value={flag.name}>{flag.name}</SelectItem>))}</SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="tipo">Tipo *</Label>
                        <Select value={formData.tipo || ''} onValueChange={(value) => handleInputChange('tipo', value)}>
                            <SelectTrigger><SelectValue placeholder="Selecione o tipo" /></SelectTrigger>
                            <SelectContent>{paymentMethods.map(pm => <SelectItem key={pm.id} value={pm.name}>{pm.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="parcelas">Parcelas</Label>
                        <Select value={formData.parcelas || '1'} onValueChange={(value) => handleInputChange('parcelas', value)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{[...Array(12)].map((_, i) => (<SelectItem key={i + 1} value={(i + 1).toString()}>{i + 1}x</SelectItem>))}</SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="valor">Valor (R$) *</Label>
                        <Input id="valor" type="text" placeholder="0,00" value={formData.valor || ''} onChange={(e) => handleInputChange('valor', e.target.value)} required />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="codigo_autorizacao">Código de Autorização</Label>
                        <Input id="codigo_autorizacao" placeholder="Digite o código de autorização" value={formData.codigo_autorizacao || ''} onChange={(e) => handleInputChange('codigo_autorizacao', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="codigopedido">Cód. de Pedido</Label>
                        <Input id="codigopedido" placeholder="Digite o código do pedido" value={formData.codigopedido || ''} onChange={(e) => handleInputChange('codigopedido', e.target.value)} />
                    </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
                    <Button type="submit" className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700">Salvar Alterações</Button>
                </div>
            </form>
        </>
    );
};

export default ReceiptsEditForm;